import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import { BASE_URL } from "../../Api/ApiUrls";
import PropertyModal from "./Jvjdmodal";

const JvJdProperties = () => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("jv/jd");
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const PROPERTY_TYPES = ["PLOT", "LAND"];

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchProperties = async (page, size, searchQuery, propertyType) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      // Server-side filtering by type (jv/jd or build to suit)
      let url = `${BASE_URL}/property/?type=${encodeURIComponent(tab)}&page=${page}&page_size=${size}`;
      
      if (propertyType) {
        url += `&property_type=${encodeURIComponent(propertyType)}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });
      const data = res.data?.data || [];
      const pagination = res.data?.pagination || {};

      return { data, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to fetch properties");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setProperties(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, propertyType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchProperties(page, size, searchQuery, propertyType);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, propertyType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchProperties(page, 100, searchQuery, propertyType);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        allProps = [...allProps, ...result.data];
        lastPagination = result.pagination;
        setProperties([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, propertyType) => {
    if (size > 100) {
      loadProgressively(page, size, searchQuery, propertyType);
    } else {
      loadPage(page, size, searchQuery, propertyType);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, selectedPropertyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedPropertyType, pageSize]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, selectedPropertyType);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property?")) return;

    try {
      await axios.delete(`${BASE_URL}/property/${id}/`);
      toast.success("Property deleted successfully!");
      runLoad(currentPage, pageSize, search, selectedPropertyType);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete property. Please try again.");
    }
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    try {
      // Update local state immediately for better UX
      setProperties(prev =>
        prev.map(property =>
          property.property_id === propertyId
            ? { ...property, Admin_status: newStatus }
            : property
        )
      );

      // Send status update to backend
      await axios.put(`${BASE_URL}/property/${propertyId}/`, { 
        Admin_status: newStatus
      });
      
      toast.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
      
      // Revert the status on error and reload
      runLoad(currentPage, pageSize, search, selectedPropertyType);
    }
  };

  const handlePropertyTypeChange = (event, newValue) => {
    setSelectedPropertyType(newValue);
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, selectedPropertyType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setSelectedPropertyType('');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center', color: 'primary.main', mb: 2 }}>
          JV / JD & Build To Suit Properties
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
          >
            <Tab label="JV / JD" value="jv/jd" />
            <Tab label="Build To Suit" value="build to suit" />
          </Tabs>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search Location / Type"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by location, property type..."
              size="small"
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && <CircularProgress size={20} />}
          </Box>
        </Box>

        {/* Property Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedPropertyType}
            onChange={handlePropertyTypeChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="property type tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 'auto',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
              }
            }}
          >
            <Tab 
              label="All Types" 
              value="" 
              sx={{ 
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              }}
            />
            {PROPERTY_TYPES.map((type) => (
              <Tab 
                key={type} 
                label={type} 
                value={type}
                sx={{ 
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {isLoading && properties.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "S.No",
                    "Prop ID",
                    "Property Type",
                    "Location",
                    "User Details",
                    "Budget",
                    "Acres",
                    "Facing",
                    "Status",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: "bold",
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {properties.map((item, index) => (
                  <TableRow key={item.property_id}>
                    <TableCell align="center">
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {`PROP${item.property_id}`}
                    </TableCell>
                    <TableCell align="center">
                      {item.property_type}
                    </TableCell>

                    <TableCell align="center">
                      {item.location}
                    </TableCell>

                    {/* Name + Mobile */}
                    <TableCell align="center">
                      <Box>
                        <Typography variant="body2">{item.user_first_name} {item.user_last_name}</Typography>
                        <Typography variant="caption" display="block">{item.user_mobile_no}</Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      ₹{item.min_budget?.toLocaleString()} - ₹
                      {item.max_budget?.toLocaleString()}
                    </TableCell>

                    <TableCell align="center">
                      {item.min_acres} - {item.max_acres}
                    </TableCell>

                    <TableCell align="center">{item.facing}</TableCell>

                    {/* STATUS DROPDOWN */}
                    <TableCell align="center">
                      <Select
                        size="small"
                        value={item.Admin_status || "Pending"}
                        onChange={(e) =>
                          handleStatusChange(item.property_id, e.target.value)
                        }
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => {
                            setSelectedRow(item);
                            setOpenModal(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>

                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(item.property_id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {properties.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}

        <PropertyModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          data={selectedRow}
        />
      </Box>
      
      <ToastContainer />
    </Container>
  );
};

export default JvJdProperties;